import { useEffect, useState } from 'react';
import { QrCode } from 'lucide-react';
import { useAuth } from '@/shared/lib/auth';
import { usePassbookQuery } from '@/shared/lib/passbook';
import { getLastPassbookId } from '@/shared/lib/last-passbook';
import { firstPassbookId } from '@/shared/lib/passbook-access';
import { frontBase, publicApiBase } from '@/shared/lib/passbook-utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/ui/card';
import { EmptyState } from '@/shared/components/empty-state';
import { Skeleton } from '@/shared/ui/skeleton';
import { Button } from '@/shared/ui/button';

export function AccountQrCodePage() {
  const { user } = useAuth();
  // A conta fica fora de /loja/:id. Usa a última loja visitada também para
  // Admin em inspeção e usuários multi-loja, cujo perfil não traz id_passbooks.
  const idPassbook =
    getLastPassbookId() ?? firstPassbookId(user);
  const { data: passbook, isLoading } = usePassbookQuery(idPassbook ?? '', { enabled: Boolean(idPassbook) });
  const [hasCustomQr, setHasCustomQr] = useState<boolean | null>(null);

  useEffect(() => {
    if (!idPassbook) {
      setHasCustomQr(null);
      return;
    }

    let active = true;
    setHasCustomQr(null);
    const url = `${publicApiBase()}/qrcode-personalizado/${idPassbook}.png`;
    fetch(url, { method: 'HEAD' })
      .then((r) => {
        if (active) setHasCustomQr(r.ok);
      })
      .catch(() => {
        if (active) setHasCustomQr(false);
      });

    return () => {
      active = false;
    };
  }, [idPassbook]);

  if (!idPassbook) {
    return (
      <EmptyState
        icon={QrCode}
        title="Selecione uma loja"
        description="O QR-Code está vinculado à loja do seu perfil. Acesse uma loja pelo painel."
        className="py-16"
      />
    );
  }

  if (isLoading || hasCustomQr === null) {
    return <Skeleton className="h-80 max-w-lg rounded-2xl" />;
  }

  const inviteUrl = `${frontBase()}/invite/${idPassbook}`;
  const customQrUrl = `${publicApiBase()}/qrcode-personalizado/${idPassbook}.png`;
  const generatedQr = `https://api.qrserver.com/v1/create-qr-code/?size=280x280&margin=8&data=${encodeURIComponent(passbook?.shortUrl ?? inviteUrl)}`;

  return (
    <Card className="max-w-lg">
      <CardHeader>
        <CardTitle>Meu QR-Code</CardTitle>
        <CardDescription>Compartilhe o cartão digital da loja.</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col items-center gap-4">
        {hasCustomQr ? (
          <>
            <img src={customQrUrl} alt="QR Code personalizado" className="h-auto w-full max-w-[280px] rounded-xl" />
            {passbook?.shortUrl && (
              <a href={passbook.shortUrl} target="_blank" rel="noreferrer" className="text-sm text-primary hover:underline">
                {passbook.shortUrl}
              </a>
            )}
            <Button variant="secondary" asChild>
              <a href={customQrUrl} download="QRCode.png" target="_blank" rel="noreferrer">
                Download do QR
              </a>
            </Button>
          </>
        ) : (
          <>
            <img
              src={generatedQr}
              alt="QR Code"
              width={280}
              height={280}
              className="h-auto w-full max-w-[280px] rounded-xl bg-white p-2"
            />
            <p className="text-center text-sm text-muted-foreground">Aponte a câmera para instalar o cartão.</p>
            <Button variant="secondary" asChild>
              <a href={generatedQr.replace('size=280x280', 'size=300x300') + '&download=1'} download="qrcode.png">
                Download do QR
              </a>
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
